export type ProviderErrorCode =
  | 'AUTH_ERROR'
  | 'RATE_LIMITED'
  | 'PROVIDER_DOWN'
  | 'TIMEOUT'
  | 'NOT_FOUND'
  | 'VALIDATION_FAILED'
  | 'UNKNOWN'

export class ProviderError extends Error {
  readonly code: ProviderErrorCode
  readonly provider: string
  readonly retryable: boolean

  constructor(opts: {
    code: ProviderErrorCode
    message: string
    provider: string
    retryable: boolean
  }) {
    super(opts.message)
    this.name = 'ProviderError'
    this.code = opts.code
    this.provider = opts.provider
    this.retryable = opts.retryable
  }
}

export interface ImageRequest {
  prompt: string
  negativePrompt?: string
  model: string
  aspectRatio?: '1:1' | '16:9' | '9:16' | '4:3' | '3:4'
  numImages?: number
}

export type VideoResolution = '720p' | '1080p' | '4k'

export interface VideoRequest {
  imageUrl?: string
  lastFrameImageUrl?: string
  prompt?: string
  model: string
  durationSeconds?: number
  aspectRatio?: '16:9' | '9:16'
  resolution?: VideoResolution
}

export type JobStatus = 'pending' | 'running' | 'succeeded' | 'failed'

export interface GenerationJob {
  jobId: string
  status: JobStatus
}

export type CheckStatusResult =
  | { status: 'succeeded'; outputUrls: string[] }
  | { status: 'failed'; error: string }
  | { status: 'pending' | 'running' }

export interface ModelCapabilities {
  durationOptions?: number[]
  resolutionOptions?: VideoResolution[]
  hasAudio?: boolean
  maxImages?: number
}

export interface Model {
  id: string
  name: string
  type: 'image' | 'video'
  provider: string
  capabilities?: ModelCapabilities
}

export interface ProviderAdapter {
  readonly provider: string
  generateImage(request: ImageRequest, apiKey: string): Promise<GenerationJob>
  generateVideo(request: VideoRequest, apiKey: string): Promise<GenerationJob>
  checkStatus(jobId: string, apiKey: string): Promise<CheckStatusResult>
  listModels(apiKey: string): Promise<Model[]>
}
